import { useCallback, useEffect, useState } from 'react'
import { ChevronLeft, ChevronRight, Download, ImageOff, X, ZoomIn } from 'lucide-react'
import { mediaUrl } from '../lib/api'
import { cx, fileSize, formatDateTime } from '../lib/format'
import type { Evidence } from '../lib/types'

const displayFile = (item: Evidence) => item.webFile || item.file

export function PhotoGallery({
  caseId,
  photos,
  className,
}: {
  caseId: string
  photos: Evidence[]
  className?: string
}) {
  const [openIndex, setOpenIndex] = useState<number | null>(null)

  const close = useCallback(() => setOpenIndex(null), [])
  const step = useCallback(
    (delta: number) =>
      setOpenIndex((current) =>
        current === null ? null : (current + delta + photos.length) % photos.length,
      ),
    [photos.length],
  )

  useEffect(() => {
    if (openIndex === null) return
    const handler = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close()
      if (event.key === 'ArrowRight') step(1)
      if (event.key === 'ArrowLeft') step(-1)
    }
    window.addEventListener('keydown', handler)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', handler)
      document.body.style.overflow = ''
    }
  }, [openIndex, close, step])

  if (!photos.length) return null
  const active = openIndex === null ? null : photos[openIndex]

  return (
    <>
      <div className={cx('grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4', className)}>
        {photos.map((photo, index) => (
          <figure key={photo.id} className="group print-avoid-break">
            <button
              type="button"
              onClick={() => setOpenIndex(index)}
              className="relative block w-full overflow-hidden rounded-lg border border-white/[0.08] bg-ink-900 transition hover:border-altair-500/50"
            >
              <img
                src={mediaUrl(caseId, displayFile(photo))}
                alt={photo.caption || photo.originalName}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover transition duration-300 group-hover:scale-[1.03]"
              />
              <span className="no-print absolute inset-0 flex items-center justify-center bg-black/45 opacity-0 transition group-hover:opacity-100">
                <ZoomIn size={20} className="text-white" />
              </span>
              <span className="no-print absolute bottom-1.5 right-1.5 rounded bg-black/70 px-1.5 py-0.5 font-mono text-[10px] text-white/80 backdrop-blur">
                {index + 1}/{photos.length}
              </span>
            </button>
            {photo.caption ? (
              <figcaption className="mt-1.5 text-[11.5px] leading-snug text-slate-400">
                {photo.caption}
              </figcaption>
            ) : null}
          </figure>
        ))}
      </div>

      {active ? (
        <div className="no-print fixed inset-0 z-50 flex animate-fade-in flex-col bg-black/92 backdrop-blur">
          <div className="flex items-center justify-between gap-3 border-b border-white/10 px-4 py-3">
            <div className="min-w-0">
              <div className="truncate text-[13.5px] font-medium text-white">
                {active.caption || active.originalName}
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-3 text-[11.5px] text-slate-500">
                <span>{active.originalName}</span>
                <span>{fileSize(active.size)}</span>
                {active.width ? (
                  <span>
                    {active.width}×{active.height}
                  </span>
                ) : null}
                {active.capturedAt ? <span>Captured {formatDateTime(active.capturedAt)}</span> : null}
              </div>
            </div>
            <div className="flex items-center gap-1.5">
              <a
                href={mediaUrl(caseId, active.file)}
                download={active.originalName}
                className="btn-ghost px-2.5 py-2"
                title="Download original"
              >
                <Download size={15} />
              </a>
              <button type="button" onClick={close} className="btn-ghost px-2.5 py-2" aria-label="Close">
                <X size={16} />
              </button>
            </div>
          </div>

          <div className="relative flex min-h-0 flex-1 items-center justify-center p-4">
            <img
              src={mediaUrl(caseId, displayFile(active))}
              alt={active.caption || active.originalName}
              className="max-h-full max-w-full rounded-lg object-contain shadow-lift"
            />
            {photos.length > 1 ? (
              <>
                <button
                  type="button"
                  onClick={() => step(-1)}
                  className="absolute left-3 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-black/60 text-white transition hover:bg-black/80"
                  aria-label="Previous photo"
                >
                  <ChevronLeft size={22} />
                </button>
                <button
                  type="button"
                  onClick={() => step(1)}
                  className="absolute right-3 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-black/60 text-white transition hover:bg-black/80"
                  aria-label="Next photo"
                >
                  <ChevronRight size={22} />
                </button>
              </>
            ) : null}
          </div>

          <div className="flex items-center justify-center gap-2 overflow-x-auto border-t border-white/10 px-4 py-3">
            {photos.map((photo, index) => (
              <button
                key={photo.id}
                type="button"
                onClick={() => setOpenIndex(index)}
                className={cx(
                  'h-12 w-16 shrink-0 overflow-hidden rounded border transition',
                  index === openIndex
                    ? 'border-altair-500 opacity-100'
                    : 'border-white/10 opacity-50 hover:opacity-90',
                )}
              >
                <img
                  src={mediaUrl(caseId, displayFile(photo))}
                  alt=""
                  className="h-full w-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </>
  )
}

export function NoPhotos() {
  return (
    <div className="flex items-center gap-2.5 rounded-lg border border-white/[0.07] bg-white/[0.02] px-4 py-3 text-[13px] text-slate-500">
      <ImageOff size={15} />
      No still photos were attached to this case.
    </div>
  )
}
